import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { apiWorker, getNodeWithFallback } from '../api/worker';
import { NodeConfig, Node, RoleDefinition, DirectoryMember } from '../types';
import toast from 'react-hot-toast';

export const checkNodeNameAvailable = async (name: string): Promise<boolean> => {
  const trimmed = name.trim();
  if (!trimmed) return false;
  try {
    const res = await apiWorker.get(`/api/nodes/check-name?name=${encodeURIComponent(trimmed)}`);
    return Boolean(res.data?.available);
  } catch {
    return false;
  }
};

export const useCheckNodeName = (name: string) => {
  const trimmed = name.trim();
  return useQuery({
    queryKey: ['node-name-available', trimmed],
    queryFn: () => checkNodeNameAvailable(trimmed),
    enabled: trimmed.length >= 2,
    staleTime: 10_000,
  });
};

export const useCreateNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { 
      name: string; 
      slug: string; 
      template: string; 
      visibility: string; 
      tier: string;
      modules?: string[];
      config?: Partial<NodeConfig>;
    }) => 
      apiWorker.post('/api/nodes', data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-mock'] });
      toast.success('Organization created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create organization');
    },
  });
};

export const useUpdateNodeModules = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (modulesMap: Record<string, boolean>) => 
      apiWorker.patch(`/api/nodes/${nodeId}/modules`, modulesMap).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
      toast.success('Modules updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update modules');
    },
  });
};

export const useUpdateNodeConfig = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Partial<NodeConfig>) => {
      // Preserve existing node.config keys so partial updates don't wipe unrelated settings
      const cachedQueries = queryClient.getQueriesData<{ node?: Node }>({ queryKey: ['node-config-full'] });
      let existingConfig: Record<string, any> = {};
      for (const [, data] of cachedQueries) {
        if (data?.node?.id === nodeId && data.node.config) {
          existingConfig = data.node.config;
          break;
        }
      }
      const mergedConfig = { ...existingConfig, ...config };
      return apiWorker.patch(`/api/nodes/${nodeId}/config`, mergedConfig).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
      toast.success('Settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    },
  });
};

export const useUploadNodeLogo = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiWorker.post(`/api/nodes/${nodeId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
      toast.success('Logo uploaded successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload logo');
    },
  });
};

export const useUpdateNodeVisibility = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (visibility: string) => 
      apiWorker.patch(`/api/nodes/${nodeId}/visibility`, { visibility }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
      toast.success('Visibility updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update visibility');
    },
  });
};

export const useNodeRoles = (nodeId: string) => {
  return useQuery<RoleDefinition[]>({
    queryKey: ['node-roles', nodeId],
    queryFn: () => {
      if (nodeId === 'platform-root') return [];
      return apiWorker.get(`/api/nodes/${nodeId}/roles`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    enabled: !!nodeId && nodeId !== 'platform-root',
  });
};

export const useUpdateRoleDefinitions = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roles: RoleDefinition[]) => 
      apiWorker.patch(`/api/nodes/${nodeId}/roles`, roles).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-roles', nodeId] });
      toast.success('Roles updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update roles');
    },
  });
};

export const useCreateChildNode = (parentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { 
      name: string; 
      slug: string; 
      visibility: string; 
    }) => 
      apiWorker.post(`/api/nodes/${parentId}/children`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sub-nodes', parentId] });
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-mock'] });
      toast.success('Sub-group created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create sub-group');
    },
  });
};

export const useLeadership = (nodeId: string) => {
  return useQuery<DirectoryMember[]>({
    queryKey: ['leadership', nodeId],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/members/leadership`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId && nodeId !== 'platform-root',
  });
};

export const useTenantBySlug = (slug: string) => {
  return useQuery<Node>({
    queryKey: ['node-by-slug', slug],
    queryFn: () => getNodeWithFallback(slug),
    enabled: !!slug,
  });
};

export const useNodeLineage = (nodeId: string) => {
  return useQuery<Node[]>({
    queryKey: ['node-lineage', nodeId],
    queryFn: async () => {
      if (nodeId === 'platform-root') return [];
      const response = await apiWorker.get(`/api/nodes/${nodeId}/lineage`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!nodeId && nodeId !== 'platform-root',
  });
};

export const useSubNodes = (nodeId: string) => {
  return useQuery<Node[]>({
    queryKey: ['sub-nodes', nodeId],
    queryFn: async () => {
      if (nodeId === 'platform-root') return [];
      const response = await apiWorker.get(`/api/nodes/${nodeId}/tree`);
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.content)) return data.content; // PaginatedResponse fallback
      if (data && Array.isArray(data.children)) return data.children;
      if (data && Array.isArray(data.subNodes)) return data.subNodes;
      return [];
    },
    enabled: !!nodeId && nodeId !== 'platform-root',
  });
};

export const useDescendantCount = (nodeId: string) => {
  return useQuery<{ nodeCount: number; memberCount: number }>({
    queryKey: ['node-descendants', nodeId],
    queryFn: () => {
      if (nodeId === 'platform-root') return { nodeCount: 0, memberCount: 0 };
      return apiWorker.get(`/api/nodes/${nodeId}/analytics/counts`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    enabled: !!nodeId && nodeId !== 'platform-root',
  });
};

export const useNodeFullTree = (nodeId: string) => {
  return useQuery<any>({
    queryKey: ['node-full-tree', nodeId],
    queryFn: () => {
      if (!nodeId) return null;
      return apiWorker.get(`/api/nodes/${nodeId}/tree`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    enabled: !!nodeId,
  });
};
